import { Link } from "react-router-dom";
import { useBrandSettings } from "@/hooks/useBrandSettings";

interface LogoProps {
  variant?: "header" | "footer" | "admin" | "favicon";
  className?: string;
}

export default function Logo({ variant = "header", className = "" }: LogoProps) {
  const { settings } = useBrandSettings();

  const isAdmin = variant === "admin";
  const isFooter = variant === "footer";
  const textColor = isAdmin || isFooter ? "text-white" : "text-hj-dark";
  const subTextColor = isAdmin || isFooter ? "text-gray-400" : "text-hj-muted";

  // Use uploaded logo if available
  if (variant === "header" && settings.logoUrl) {
    return (
      <Link to="/" className={`flex items-center gap-2 md:gap-3 shrink-0 ${className}`}>
        <img
          src={settings.logoUrl}
          alt={settings.siteName}
          className="h-8 md:h-10 lg:h-12 w-auto object-contain shrink-0"
        />
        <div className="min-w-0">
          <h1 className="font-heading text-base md:text-xl lg:text-2xl font-bold text-hj-dark tracking-tight leading-none truncate">
            {settings.siteName}
          </h1>
          {settings.slogan && (
            <p className="text-[10px] md:text-xs text-hj-muted font-body mt-0.5 tracking-wide truncate">
              {settings.slogan}
            </p>
          )}
        </div>
      </Link>
    );
  }

  if (variant === "footer" && settings.logoDarkUrl) {
    return (
      <Link to="/" className={`inline-flex items-center gap-3 ${className}`}>
        <img
          src={settings.logoDarkUrl}
          alt={settings.siteName}
          className="h-10 w-auto object-contain"
        />
      </Link>
    );
  }

  if (variant === "admin" && settings.logoUrl) {
    return (
      <Link to="/" className={`flex items-center gap-3 ${className}`}>
        <img
          src={settings.logoUrl}
          alt={settings.siteName}
          className="h-8 w-auto object-contain"
        />
        <span className="font-bold text-lg text-white">Admin</span>
      </Link>
    );
  }

  // Default: built-in premium SVG logo
  return (
    <Link to="/" className={`flex items-center gap-2 md:gap-3 shrink-0 min-w-0 ${className}`}>
      {/* Swiss-inspired abstract logo mark */}
      <div className="relative w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 shrink-0">
        <svg
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full"
        >
          {/* Outer rounded square frame */}
          <rect
            x="2"
            y="2"
            width="44"
            height="44"
            rx="8"
            stroke={settings.accentColor || "#8B0000"}
            strokeWidth="2.5"
            fill="none"
          />
          {/* Abstract Swiss cross - stylized as pulse/radar lines */}
          <path
            d="M24 8 L24 20"
            stroke={settings.accentColor || "#8B0000"}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M24 28 L24 40"
            stroke={settings.accentColor || "#8B0000"}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M8 24 L20 24"
            stroke={settings.accentColor || "#8B0000"}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M28 24 L40 24"
            stroke={settings.accentColor || "#8B0000"}
            strokeWidth="3"
            strokeLinecap="round"
          />
          {/* Center dot - journal/eye metaphor */}
          <circle
            cx="24"
            cy="24"
            r="3"
            fill={settings.accentColor || "#8B0000"}
          />
          {/* Subtle corner accents - Swiss precision */}
          <path
            d="M6 6 L12 6 L6 12 Z"
            fill={settings.primaryColor || "#0F172A"}
            opacity="0.15"
          />
          <path
            d="M42 6 L36 6 L42 12 Z"
            fill={settings.primaryColor || "#0F172A"}
            opacity="0.15"
          />
          <path
            d="M6 42 L12 42 L6 36 Z"
            fill={settings.primaryColor || "#0F172A"}
            opacity="0.15"
          />
          <path
            d="M42 42 L36 42 L42 36 Z"
            fill={settings.primaryColor || "#0F172A"}
            opacity="0.15"
          />
        </svg>
      </div>

      {/* Wordmark — visible on all screen sizes */}
      <div className="min-w-0">
        <h1
          className={`font-heading text-base md:text-xl lg:text-2xl font-bold ${textColor} tracking-tight leading-none truncate`}
        >
          {settings.siteName}
        </h1>
        {settings.slogan && (
          <p className={`text-[10px] md:text-xs ${subTextColor} font-body mt-0.5 tracking-wide truncate`}>
            {settings.slogan}
          </p>
        )}
      </div>
    </Link>
  );
}